import axios from "axios";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { useParams } from "react-router-dom";
import Actions from "../../../../components/Common/Actions";
import AntdBreadcrumbs from "../../../../components/Common/Breadcrumbs/AntdBreadcrumbs";
import FilledButton from "../../../../components/Common/Buttons/FilledButton";
import DoctorsFilterOptions from "../../../../components/Common/CommonFilterOptions";
import { convertSlugToNormalString } from "../../../../components/Constants";
import ListPageLayout from "../../../../components/Layout";
import Deletepopup from "../../../../components/Modal/Deletepopup";
import useDebouncedSearch from "../../../../components/Utils/hooks/useDebouncedSearch";
import useDeleteHandler from "../../../../components/Utils/hooks/useDeleteHandler";
import usePageChange from "../../../../components/Utils/hooks/usePageChange";
import usePaginatedData from "../../../../components/Utils/hooks/usePaginatedData";
import useSortHandler from "../../../../components/Utils/hooks/useSortHandler";

const OnlineViewHealthConcern = () => {
  const navigate = useNavigate();
  const { id: doctorSpecialization } = useParams();

  const addNewHealthConcern = {
    id: "addnewpost",
    onClick: () =>
      navigate(`/online-doctor-add-health-concern/${doctorSpecialization}`),
    style: { width: "240px" },
    label: "Add New Health Concern",
  };

  // Sorting
  const [sort, setSort] = useState("");
  const [sortName, setSortName] = useState("Newest First");
  const handleSortClick = useSortHandler(setSort, setSortName);

  // Search
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedSearch(setSearch, () =>
    setPagination((prev) => ({ ...prev, skip: 0, currentPage: 1 })),
  );

  // Delete state
  const [deleteId, setDeleteId] = useState("");
  const [showDelete, setShowDelete] = useState(false);

  // Pagination
  const { records, totalCount, pagination, setPagination, fetchData } =
    usePaginatedData({
      apiUrl: `${process.env.REACT_APP_API_URL}doctor-health-concern/list`,
      initialParams: {
        sort,
        searchText: search,
        online_doctor_specialisation_slug: doctorSpecialization,
      },
    });

  const handlePageChange = usePageChange(pagination, setPagination);
  const handleDelete = useDeleteHandler(
    "doctor-health-concern",
    fetchData,
    setShowDelete,
  );

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.skip, pagination.limit, sort, search]);

  const handleActive = async (id, status) => {
    try {
      await axios.patch(
        `${process.env.REACT_APP_API_URL}doctor-health-concern/update/active/${id}`,
        {
          active: !status,
        },
      );
      fetchData();
    } catch (err) {
      console.error(err);
    }
  };

  const columns = [
    {
      title: "S.No",
      render: (text, record, index) =>
        Number(pagination.skip || 0) + (index + 1),
      key: "serial",
      width: 80,
      fixed: "left",
    },
    {
      title: "Health Concern",
      dataIndex: "health_concern",
      key: "health_concern",
      width: 200,
    },
    {
      title: "Image / Icon",
      dataIndex: "image",
      key: "image",
      render: (image) => image || "-",
      width: 150,
    },
    {
      title: "Status",
      dataIndex: "active",
      key: "active",
      render: (active) => (active ? "Verified" : "Not Verified"),
      width: 150,
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Actions
          recordActiveStatus={record.active}
          isDelete={true}
          isEdit={true}
          isActive={true}
          onClickDelete={() => {
            setDeleteId(record._id);
            setShowDelete(true);
          }}
          onClickEdit={() =>
            navigate(
              `/online-doctor-health-concern-edit/${doctorSpecialization}/${record._id}`,
            )
          }
          onClickActive={() => handleActive(record._id, record.active)}
        />
      ),
    },
  ];

  const dataSource = records.map((el) => ({ ...el, key: el._id }));

  const breadcrumbsList = [
    { text: "Doctor", link: "/online-doctor-specialisation" },
    {
      text: "Online Doctor Specialization",
      link: "/online-doctor-specialisation",
    },
    { text: convertSlugToNormalString(doctorSpecialization), link: "" },
    { text: "View", link: "" },
  ];

  return (
    <ListPageLayout
      breadcrumbs={
        <AntdBreadcrumbs
          className="healthsyMasterBreadCrumbs"
          breadcrumbsList={breadcrumbsList}
        />
      }
      addButton={<FilledButton props={addNewHealthConcern} />}
      filters={
        <DoctorsFilterOptions
          limit={pagination.limit}
          setLimit={(val) =>
            setPagination((prev) => ({
              ...prev,
              limit: val,
              skip: 0,
              currentPage: 1,
            }))
          }
          search={search}
          onChange={debouncedSearch}
          handleSortClick={handleSortClick}
          sortName={sortName}
          filter_records={() => {}}
          setFilterShow={() => {}}
          showFilter={false}
        />
      }
      tableProps={{
        columns,
        dataSource,
        className: "mx-3 pt-2 table-scrollable medicineList_table",
      }}
      totalCount={totalCount}
      pagination={pagination}
      onPageChange={(selected) => handlePageChange({ selected })}
    >
      {showDelete && (
        <Deletepopup
          handleDelete={() => handleDelete(deleteId)}
          id={deleteId}
          handleClose={() => setShowDelete(false)}
        />
      )}
    </ListPageLayout>
  );
};

export default OnlineViewHealthConcern;
